import moment from "moment";

import { queryKeys } from "../queryKeys";
import { getMargin } from "../rest/account";
import {
  getHighYieldCashAccruedInterest,
  getHighYieldCashAprTier,
  getHighYieldCashStatus,
  type HighYieldCashAccruedInterestSummary,
  type HighYieldCashAprTier,
  type HighYieldCashStatusResponse,
} from "../rest/hyc";
import useRequest, { type QueryOptions } from "./useRequest";

const HYC_COMPOUNDING_PERIODS = 12;

export const getHighYieldRate = (aprBps?: number) => {
  const apy = aprBps
    ? ((1 + aprBps / 10000 / HYC_COMPOUNDING_PERIODS) **
        HYC_COMPOUNDING_PERIODS -
        1) *
      100
    : undefined;

  return { hycApyString: apy?.toFixed(2) ?? "-", hycApyNumber: apy };
};

export const useGetHYCAccountStatus = ({
  accountID,
  options,
}: {
  accountID?: string;
  options?: QueryOptions<HighYieldCashStatusResponse>;
}) => {
  const { data, ...rest } = useRequest(
    queryKeys.highYieldCash.status(accountID || ""),
    () => getHighYieldCashStatus(accountID ?? ""),
    {
      ...options,
      enabled: options?.enabled && !!accountID,
    },
  );

  const aprBps = data?.tier_assignment?.account_rate_bps;

  return {
    hycStatus: data?.status ?? null,
    hycTierAssignment: data?.tier_assignment ?? null,
    ...getHighYieldRate(aprBps),
    ...rest,
  };
};

export const useGetHYCAprTierOffer = ({
  accountID,
  options,
}: {
  accountID?: string;
  options?: QueryOptions<HighYieldCashAprTier>;
}) => {
  const { data, ...rest } = useRequest(
    queryKeys.highYieldCash.aprTier(accountID || ""),
    () => getHighYieldCashAprTier(accountID ?? ""),
    {
      ...options,
      enabled: options?.enabled && !!accountID,
    },
  );

  return {
    ...rest,
    hycTierId: data?.tier_id ?? null,
    hycTierName: data?.tier_name ?? null,
    hycAprTierBps: data?.account_rate_bps ?? null,
    ...getHighYieldRate(data?.account_rate_bps),
  };
};

export const useGetHYCAccruedInterest = ({
  accountID,
  options,
}: {
  accountID?: string;
  options?: QueryOptions<HighYieldCashAccruedInterestSummary[]>;
}) => {
  return useRequest(
    queryKeys.highYieldCash.accruedInterest(accountID || ""),
    async () => {
      if (!accountID) {
        throw new Error("accountID required");
      }
      const lastMargin = await getMargin(accountID, false, true);
      const dates = [moment()];
      if (lastMargin?.balance_asof) {
        dates.push(moment(lastMargin?.balance_asof));
      }
      return await getHighYieldCashAccruedInterest(accountID, dates);
    },
    {
      ...options,
      enabled: options?.enabled && !!accountID,
    },
  );
};
